import {
  DataStrategyFunction,
  DataStrategyMatch,
  isRouteErrorResponse,
  matchPath,
  PathPattern,
  redirect,
  RouteObject,
  ActionFunctionArgs as RouterActionFunctionArgs,
  LoaderFunctionArgs as RouterLoaderFunctionArgs,
  Params as RouterParams,
  useActionData,
  useLoaderData,
  useMatch,
  useNavigation,
  useParams as useRouterParams,
  useRouteError as useRouterRouteError,
} from 'react-router-dom';
import { z } from 'zod';
import { AppStore, stateSchema } from '~/stores/app';
import { ClaimType } from './constants';
import { logError } from './observability';
import { crewOrderSchema, draftClaimLineItemSchema } from './schemas';
import { OverrideProperties, Promisable } from './types';

export const useIsLoading = () => {
  const navigation = useNavigation();

  return navigation.state === 'submitting' || navigation.state === 'loading';
};

// ? normalize as error or response error???
export const useRouteError = () => {
  const error = useRouterRouteError();
  if (error instanceof Error) {
    return error;
  }

  if (isRouteErrorResponse(error)) {
    return new Error('Response Error', { cause: { originalError: error } });
  }

  return new Error('Unknown error occurred', {
    cause: { originalError: error },
  });
};

export const useParams = <Params = RouterParams>(schema?: z.Schema<Params>) => {
  const params = useRouterParams();

  return schema ? schema.parse(params) : params;
};

export { redirect };

export const useIsAtIndex = () => {
  const { store = '' } = useParams(z.object({ store: z.string().optional() }));
  const pathMatch = useMatch(`/${store}`);

  return Boolean(pathMatch);
};

export const catchallRoute: RouteObject = {
  path: '*',
  loader: ({ params: { store = '' } }) => redirect(`/${store}`),
};

/**
 * Check the path against the current request URL and return a 200 response if it matches.
 * Otherwise, redirect to the base route.
 * This is useful for routes that are not renderable and should not be accessed directly.
 * For example, the path /order will match as /:store,
 * the app and will try to get settings for the store 'order' instead of hitting the catchall route.
 */
export const nonRenderingRouteLoader =
  (path: string | PathPattern<string>) =>
  ({ request, params }: RouterLoaderFunctionArgs) => {
    const pathMatch = matchPath(path, new URL(request.url).pathname);

    return pathMatch ? { ok: true } : redirect(`/${params.store ?? ''}`);
  };

// Data Strategy Middleware
export type MiddlewareContext = {
  // TODO figure out how to make the app not optionally undefined
  appStore?: AppStore;
};

const middlewareContextSchema = z.object({
  // undefined is allowed with unknown, so we need to refine it
  appStore: z.unknown().refine((value) => !!value),
});

const middlewareFnSchema = z
  .function()
  .args(
    z.object({
      request: z.instanceof(Request),
      params: z.record(z.string(), z.union([z.string(), z.undefined()])),
    }),
    z.object({ appStore: z.unknown() }),
  )
  .returns(z.promise(z.object({ appStore: z.unknown() })));

export type MiddlewareFunction = (
  { request, params }: { request: Request; params: RouterParams },
  context: MiddlewareContext,
) => Promise<MiddlewareContext>;

type MatchWithMiddleware = OverrideProperties<
  DataStrategyMatch,
  {
    route: OverrideProperties<
      DataStrategyMatch['route'],
      { handle: { middleware: MiddlewareFunction } }
    >;
  }
>;

const hasMiddlewareFn = (
  match: DataStrategyMatch,
): match is MatchWithMiddleware =>
  !match?.route?.handle ?
    false
  : z
      .object({
        middleware: middlewareFnSchema,
      })
      .safeParse(match.route.handle).success;
/**
 * Data Strategy to enable middleware and context for data functions.
 * ! Middleware functions run sequentially and can potentially hurt performance. Be judicious.
 * Inspiration (https://reactrouter.com/6.28.2/routers/create-browser-router#middleware)
 */
export const dataStrategy: DataStrategyFunction = async ({
  request,
  params,
  matches,
}) => {
  // Run middleware **sequentially** and let them add data to `context`
  const context: MiddlewareContext = await matches
    .filter(hasMiddlewareFn)
    .reduce<
      Promise<MiddlewareContext>
    >(async (ctx, match) => match.route.handle.middleware({ request, params }, await ctx), Promise.resolve({}));

  // Run loaders in **parallel** with the `context` value
  const results = await Promise.all(
    matches
      .filter((m) => m.shouldLoad)
      .map((match) =>
        // context passed to `handler` will be passed as the 2nd parameter to loader/action
        match
          .resolve((handler) => handler(context))
          .then((result) => [match.route.id, result] as const),
      ),
  );

  return Object.fromEntries(results);
};

type DataFnArgs<
  Context = unknown,
  Params extends RouterParams = RouterParams,
> = {
  request: Request;
  params: Params;
} & (Context extends NonNullable<unknown> ? { context: Context }
: { context?: Context });

type Data = NonNullable<unknown>;
type DataFnValue<T extends Data> = T | Response | Error; // this differs from `DataFunctionValue` of `react-router-dom`, as we omit `null` and include `Error`
type DataFnReturnValue<T extends Data> = Promisable<DataFnValue<T>>;

type LoaderFnArgs<Context = unknown> = DataFnArgs<Context>;
type LoaderFn<Context, T extends Data> = (
  args: LoaderFnArgs<Context>,
  appStore: AppStore,
) => DataFnReturnValue<T>;

type ActionOkResult = { ok: true };
type ActionErrorResult = {
  ok: false;
  message?: string;
};
export type ActionResult = ActionOkResult | ActionErrorResult;
type ActionFnArgs<Context = unknown> = DataFnArgs<Context> & {
  formData: FormData;
};
type ActionFn<Context, T extends ActionResult = ActionResult> = (
  args: ActionFnArgs<Context>,
  appStore: AppStore,
) => DataFnReturnValue<T>;

function assertAppStore(
  handle: unknown,
): asserts handle is Required<MiddlewareContext> {
  if (!middlewareContextSchema.safeParse(handle).success) {
    throw new Error('App store not found in context');
  }
}

//
// Basic loader and action functions for React Router
//

/**
 * Creates a loader function that can be used with React Router.
 * It provides a `useLoaderData` hook to access the data returned by the loader.
 * If the loader function returns a `Response`, it will be returned as is.
 * If the loader function returns an `Error`, it will be thrown and handled by React Router.
 * @param fn - The loader function to be used with React Router.
 * The function should accept an object with `request` and `params` properties, and the app store as the second argument.
 * The function should return a value that is compatible with `DataFunctionValue` from `react-router-dom`.
 * The function can also return an `Error` or a `Response`.
 * @returns An object with a `useLoaderData` hook to access the data returned by the loader,
 * and a `loader` function to be used with React Router.
 */
export const makeLoader = <T extends Data>(fn: LoaderFn<unknown, T>) => ({
  useLoaderData: () => useLoaderData() as T,
  loader: async (args: RouterLoaderFunctionArgs<unknown>, handle: unknown) => {
    assertAppStore(handle);

    const result = await fn({ ...args }, handle.appStore);

    if (result instanceof Error) throw result;

    return result;
  },
});

/**
 * Creates an action function that can be used with React Router.
 * It provides a `useActionResult` hook to access the `ActionResult`.
 * If the action function returns an `Error`, it will be thrown and handled by React Router.
 * @param fn - The action function to be used with React Router.
 * The function should accept an object with `request`, `params`, and `formData` properties, and the app store as the second argument.
 * The function should return a value that is compatible with `ActionResult`.
 * The function can also return an `Error`.
 * @returns An object with a `useActionResult` hook to access the action result,
 * and an `action` function to be used with React Router.
 */
export const makeAction = <T extends ActionResult>(
  fn: ActionFn<unknown, T>,
) => ({
  useActionResult: () => useActionData() as T | undefined,
  action: async (args: RouterActionFunctionArgs<unknown>, handle: unknown) => {
    assertAppStore(handle);

    const result = await fn(
      { ...args, formData: await args.request.formData() },
      handle.appStore,
    );

    if (result instanceof Error) throw result;

    return result;
  },
});

//
// Registration loaders and actions
//

const registrationContextSchema = stateSchema
  .pick({
    settings: true,
    registrations: true,
  })
  .extend({
    settings: stateSchema.shape.settings.unwrap(),
  });

type RegistrationContext = z.infer<typeof registrationContextSchema>;

/**
 * Creates a loader function that can be used with React Router for registration-related routes.
 * This loader function is designed to work with the registration context from the app store state that includes
 * `settings` and `registrations`.
 * It provides a `useLoaderData` hook to access the data returned by the loader.
 * If the loader function returns a `Response`, it will be returned as is.
 * If the loader function returns an `Error`, it will be thrown and handled by React Router.
 * @param fn - The loader function to be used with React Router for the route.
 * The function should accept an object with `request`, `params`, and `context` properties, and the app store as the second argument.
 * The context will assert the registration context schema, ensuring that the `settings` and `registrations` are present.
 * The function should return a value that is compatible with `DataFunctionValue` from `react-router-dom`.
 * The function can also return an `Error` or a `Response`.
 * @returns An object with a `useLoaderData` hook to access the data returned by the loader,
 * and a `loader` function to be used with React Router.
 */
export const makeRegistrationLoader = <T extends Data>(
  fn: LoaderFn<RegistrationContext, T>,
) =>
  makeLoader((args, appStore) => {
    const parsedContext = registrationContextSchema.safeParse(
      appStore.getState(),
    );
    if (!parsedContext.success) {
      logError('Registration context schema failed', {
        extra: { error: parsedContext.error },
      });

      return Error('Registration context schema failed', {
        cause: parsedContext.error,
      });
    }

    return fn({ ...args, context: parsedContext.data }, appStore);
  });

/**
 * Creates an action function that can be used with React Router for registration-related actions.
 * This action function is designed to work with the registration context from the app store state that includes
 * `settings` and `registrations`.
 * It provides a `useActionResult` hook to access the action result.
 * If the action function returns an `Error`, it will be thrown and handled by React Router.
 * This action function also validates the registration context from the app store state.
 * @param fn - The action function to be used with React Router.
 * The function should accept an object with `request`, `params`, `formData`, and `context` properties, and the app store as the second argument.
 * The context will assert the registration context schema, ensuring that the `settings` and `registrations` are present.
 * The function should return a value that is compatible with `ActionResult`.
 * The function can also return an `Error`.
 * @returns An object with a `useActionResult` hook to access the action result,
 * and an `action` function to be used with React Router.
 */
export const makeRegistrationAction = <T extends ActionResult>(
  fn: ActionFn<RegistrationContext, T>,
) =>
  makeAction((args, appStore) => {
    const parsedContext = registrationContextSchema.safeParse(
      appStore.getState(),
    );
    if (!parsedContext.success) {
      logError('Registration context schema failed', {
        extra: { error: parsedContext.error },
      });

      return Error('Registration context schema failed', {
        cause: parsedContext.error,
      });
    }

    return fn({ ...args, context: parsedContext.data }, appStore);
  });

//
// Shipping Protection Claim loaders and actions
//

const shippingProtectionClaimContextSchema = stateSchema
  .pick({
    order: true,
    shippingProtectionClaim: true,
  })
  .extend({
    order: stateSchema.shape.order.unwrap(),
  });

type ShippingProtectionClaimContext = z.infer<
  typeof shippingProtectionClaimContextSchema
>;

/**
 * Creates a loader function that can be used with React Router for shipping protection claim-related routes.
 * This loader function is designed to work with the shipping protection claim context from the app store state that includes
 * `order` and `shippingProtectionClaim`.
 * It provides a `useLoaderData` hook to access the data returned by the loader.
 * If the loader function returns a `Response`, it will be returned as is.
 * If the loader function returns an `Error`, it will be thrown and handled by React Router.
 * This loader function also validates the shipping protection claim context from the app store state.
 * @param fn - The loader function to be used with React Router.
 * The function should accept an object with `request`, `params`, and `context` properties, and the app store as the second argument.
 * The context will assert the shipping protection claim context schema, ensuring that the `order` and `shippingProtectionClaim` are present.
 * The function should return a value that is compatible with `DataFunctionValue` from `react-router-dom`.
 * The function can also return an `Error` or a `Response`.
 * @returns An object with a `useLoaderData` hook to access the data returned by the loader,
 * and a `loader` function to be used with React Router.
 */
export const makeShippingProtectionClaimLoader = <T extends Data>(
  fn: LoaderFn<ShippingProtectionClaimContext, T>,
) =>
  makeLoader(({ params, ...args }, appStore) => {
    const parsedContext = shippingProtectionClaimContextSchema.safeParse(
      appStore.getState(),
    );

    if (!parsedContext.success) {
      logError('Shipping protection claim context schema failed', {
        extra: { error: parsedContext.error },
      });

      const { store } = params;
      // filter out 'reorder' from store slug
      return redirect(`/${store && store !== 'reorder' ? store : ''}`);
    }

    return fn({ ...args, params, context: parsedContext.data }, appStore);
  });

/**
 * Creates an action function that can be used with React Router for shipping protection claim-related actions.
 * This action function is designed to work with the shipping protection claim context from the app store state that includes
 * `order` and `shippingProtectionClaim`.
 * It provides a `useActionResult` hook to access the action result.
 * If the action function returns an `Error`, it will be thrown and handled by React Router.
 * This action function also validates the shipping protection claim context from the app store state.
 * @param fn - The action function to be used with React Router.
 * The function should accept an object with `request`, `params`, `formData`, and `context` properties, and the app store as the second argument.
 * The context will assert the shipping protection claim context schema, ensuring that the `order` and `shippingProtectionClaim` are present.
 * The function should return a value that is compatible with `ActionResult`.
 * The function can also return an `Error`.
 * @returns An object with a `useActionResult` hook to access the action result,
 * and an `action` function to be used with React Router.
 * */
export const makeShippingProtectionClaimAction = <T extends ActionResult>(
  fn: ActionFn<ShippingProtectionClaimContext, T>,
) =>
  makeAction((args, appStore) => {
    const parsedContext = shippingProtectionClaimContextSchema.safeParse(
      appStore.getState(),
    );

    if (!parsedContext.success) {
      logError('Shipping protection claim context schema failed', {
        extra: { error: parsedContext.error },
      });

      return new Error('Shipping protection claim context schema failed', {
        cause: parsedContext.error,
      });
    }

    return fn({ ...args, context: parsedContext.data }, appStore);
  });

//
// Crew loaders and actions
//

const crewContextSchema = stateSchema
  .pick({
    settings: true,
    order: true,
    crewClaim: true,
  })
  .extend({
    settings: stateSchema.shape.settings.unwrap(),
    order: crewOrderSchema,
  });

type CrewContext = z.infer<typeof crewContextSchema>;

/**
 * Creates a loader function that can be used with React Router for crew-related routes where the crew claim is optional.
 * This loader function is designed to work with the crew context from the app store state that includes
 * `settings`, `order` as a crew order, and nullable `crewClaim`.
 * It provides a `useLoaderData` hook to access the data returned by the loader.
 * If the loader function returns a `Response`, it will be returned as is.
 * If the loader function returns an `Error`, it will be thrown and handled by React Router.
 * This loader function also validates the crew context from the app store state.
 * @param fn - The loader function to be used with React Router.
 * The function should accept an object with `request`, `params`, and `context` properties, and the app store as the second argument.
 * The context will assert the crew context schema, ensuring that the `settings`, `order` is a crew order.
 * The function should return a value that is compatible with `DataFunctionValue` from `react-router-dom`.
 * The function can also return an `Error` or a `Response`.
 * @returns An object with a `useLoaderData` hook to access the data returned by the loader,
 * and a `loader` function to be used with React Router.
 */
export const makeCrewLoader = <T extends Data>(fn: LoaderFn<CrewContext, T>) =>
  makeLoader((args, appStore) => {
    const parsedContext = crewContextSchema.safeParse(appStore.getState());

    if (!parsedContext.success) {
      logError('Crew context schema failed', {
        extra: { error: parsedContext.error },
      });

      return redirect(`/${args.params.store}`);
    }

    return fn({ ...args, context: parsedContext.data }, appStore);
  });

/**
 * Creates an action function that can be used with React Router for crew-related actions.
 * This action function is designed to work with the crew context from the app store state that includes
 * `settings`, `order` as a crew order, and nullable `crewClaim`.
 * It provides a `useActionResult` hook to access the action result.
 * If the action function returns an `Error`, it will be thrown and handled by React Router.
 * This action function also validates the crew context from the app store state.
 * @param fn - The action function to be used with React Router.
 * The function should accept an object with `request`, `params`, `formData`, and `context` properties, and the app store as the second argument.
 * The context will assert the crew context schema, ensuring that the `settings`, `order` is a crew order.
 * The function should return a value that is compatible with `ActionResult`.
 * The function can also return an `Error`.
 * @returns An object with a `useActionResult` hook to access the action result,
 * and an `action` function to be used with React Router.
 */
export const makeCrewAction = <Result extends ActionResult>(
  fn: ActionFn<CrewContext, Result>,
) =>
  makeAction((args, appStore) => {
    const parsedContext = crewContextSchema.safeParse(appStore.getState());

    if (!parsedContext.success) {
      return new Error('Order required for crew action');
    }

    return fn({ ...args, context: parsedContext.data }, appStore);
  });

//
// Crew Claim loaders and actions
//

const crewClaimContextSchema = crewContextSchema.extend({
  crewClaim: crewContextSchema.shape.crewClaim.unwrap(),
});

type CrewClaimContext = z.infer<typeof crewClaimContextSchema>;

/**
 * Creates a loader function that can be used with React Router for crew claim-related routes.
 * This loader function is designed to work with the crew context from the app store state that includes
 * `settings`, `order` as a crew order, and `crewClaim`.
 * It provides a `useLoaderData` hook to access the data returned by the loader.
 * If the loader function returns a `Response`, it will be returned as is.
 * If the loader function returns an `Error`, it will be thrown and handled by React Router.
 * This loader function also validates the crew claim context from the app store state.
 * @param fn - The loader function to be used with React Router.
 * The function should accept an object with `request`, `params`, and `context` properties, and the app store as the second argument.
 * The context will assert the crew claim context schema, ensuring that the `settings`, `order` is a crew order, and `crewClaim` is present.
 * The function should return a value that is compatible with `DataFunctionValue` from `react-router-dom`.
 * @returns An object with a `useLoaderData` hook to access the data returned by the loader,
 * and a `loader` function to be used with React Router.
 */
export const makeCrewClaimLoader = <T extends Data>(
  fn: LoaderFn<CrewClaimContext, T>,
) =>
  makeLoader((args, appStore) => {
    const result = crewClaimContextSchema.safeParse(appStore.getState());

    if (
      !result.success ||
      result.data.crewClaim.claimType !== args.params.claimType
    ) {
      logError('Unexpected crew claim state', {
        extra: { error: result.error },
      });

      return redirect(`/${args.params.store}`);
    }

    return fn({ ...args, context: result.data }, appStore);
  });

/**
 * Creates an action function that can be used with React Router for crew claim-related actions.
 * This action function is designed to work with the crew context from the app store state that includes
 * `settings`, `order` as a crew order, and `crewClaim`.
 * It provides a `useActionResult` hook to access the action result.
 * If the action function returns an `Error`, it will be thrown and handled by React Router.
 * This action function also validates the crew claim context from the app store state.
 * @param fn - The action function to be used with React Router.
 * The function should accept an object with `request`, `params`, `formData`, and `context` properties, and the app store as the second argument.
 * The context will assert the crew claim context schema, ensuring that the `settings`, `order` is a crew order, and `crewClaim` is present.
 * The function should return a value that is compatible with `ActionResult`.
 * The function can also return an `Error`.
 * @returns An object with a `useActionResult` hook to access the action result,
 * and an `action` function to be used with React Router.
 */
export const makeCrewClaimAction = <Result extends ActionResult>(
  fn: ActionFn<CrewClaimContext, Result>,
) =>
  makeAction((args, appStore) => {
    const result = crewClaimContextSchema.safeParse(appStore.getState());

    if (!result.success) {
      logError('Unexpected crew claim state', {
        extra: { error: result.error },
      });
      return new Error('Unexpected crew claim state', {
        cause: result.error,
      });
    }

    return fn({ ...args, context: result.data }, appStore);
  });

/**
 * Creates a loader function that can be used with React Router for crew claim-related routes
 * that require claim line items.
 * This loader function is designed to work with the crew context from the app store state that includes
 * `settings`, `order` as a crew order, and `crewClaim` with at least one claim line item.
 * It provides a `useLoaderData` hook to access the data returned by the loader.
 * If the loader function returns a `Response`, it will be returned as is.
 * If the loader function returns an `Error`, it will be thrown and handled by React Router.
 * This loader function also validates the crew claim context from the app store state.
 * @param fn - The loader function to be used with React Router.
 * The function should accept an object with `request`, `params`, and `context` properties, and the app store as the second argument.
 * The context will assert the crew claim with claim line items schema, ensuring that the `settings`, `order` is a crew order, and `crewClaim` has claim line items.
 * @returns An object with a `useLoaderData` hook to access the data returned by the loader,
 * and a `loader` function to be used with React Router.
 */
export const makeCrewClaimWithClaimLineItemsLoader = <T extends Data>(
  fn: LoaderFn<CrewClaimContext, T>,
) =>
  makeCrewClaimLoader((args, appStore) => {
    const { claimLineItems } = args.context.crewClaim;

    if (claimLineItems.length === 0) {
      logError('Unexpected crew claim state - missing claim line items');

      return redirect(
        `/${args.params.store}/order/${args.context.order.idFromPlatform}`,
      );
    }

    return fn(args, appStore);
  });

//
// Draft Line Item Claim loaders and actions
//

const draftClaimLineItemContextSchema = crewClaimContextSchema.extend({
  draftClaimLineItem: stateSchema.shape.draftClaimLineItem.unwrap(),
});

type DraftClaimLineItemContext = z.infer<
  typeof draftClaimLineItemContextSchema
>;

const draftClaimLineItemsRouteParams = z.object({
  claimType: z.nativeEnum(ClaimType),
  draftId: z.string(),
  store: z.string(),
  idFromPlatform: z.string(),
});

/**
 * Creates a loader function that can be used with React Router for draft claim line item workflow.
 * This loader function is designed to work with the crew claim context from the app store state that includes
 * `settings`, `order` as a crew order, `crewClaim`, either a draft claim line item or a claim line item from
 * the `crewClaim`.
 * It provides a `useLoaderData` hook to access the data returned by the loader.
 * If the loader function returns a `Response`, it will be returned as is.
 * If the loader function returns an `Error`, it will be thrown and handled by React Router.
 * This loader function also validates the draft claim line item context from the app store state or from the `crewClaim`.
 * @param fn - The loader function to be used with React Router.
 * The function should accept an object with `request`, `params`, and `context` properties, and the app store as the second argument.
 * The context will assert the draft claim line item context schema, ensuring that the `settings`, `order` is a crew order,
 * `crewClaim` is present, and either a draft claim line item or a claim line item from the `crewClaim` is found.
 * The function should return a value that is compatible with `DataFunctionValue` from `react-router-dom`.
 * The function can also return an `Error` or a `Response`.
 * @returns An object with a `useLoaderData` hook to access the data returned by the loader,
 * and a `loader` function to be used with React Router.
 */
export const makeDraftClaimLineItemLoader = <T extends Data>(
  fn: LoaderFn<DraftClaimLineItemContext, T>,
) =>
  makeCrewClaimLoader(({ params, ...args }, appStore) => {
    const { claimType, draftId, store, idFromPlatform } =
      draftClaimLineItemsRouteParams.parse(params);
    const { draftClaimLineItem } = appStore.getState();
    const { crewClaim } = args.context;

    const draft =
      draftClaimLineItem && draftClaimLineItem.id === draftId ?
        draftClaimLineItem
      : crewClaim.claimLineItems.find((c) => c.id === draftId);

    if (!draft) {
      return redirect(`/${store}/order/${idFromPlatform}/${claimType}`);
    }

    const context = {
      ...args.context,
      draftClaimLineItem: draft,
    };

    return fn({ ...args, params, context }, appStore);
  });

/**
 * Creates an action function that can be used with React Router for draft claim line item actions.
 * This action function is designed to work with the crew claim context from the app store state that includes
 * `settings`, `order` as a crew order, `crewClaim`, either a draft claim line item or a claim line item from
 * the `crewClaim`.
 * It provides a `useActionResult` hook to access the action result.
 * If the action function returns an `Error`, it will be thrown and handled by React Router.
 * This action function also validates the draft claim line item context from the app store state or from the `crewClaim`.
 * @param fn - The action function to be used with React Router.
 * The function should accept an object with `request`, `params`, `formData`, and `context` properties, and the app store as the second argument.
 * The context will assert the draft claim line item context schema, ensuring that the `settings`, `order` is a crew order,
 * `crewClaim` is present, and either a draft claim line item or a claim line item from the `crewClaim` is found.
 * The function should return a value that is compatible with `ActionResult`.
 * The function can also return an `Error`.
 * @returns An object with a `useActionResult` hook to access the action result,
 * and an `action` function to be used with React Router.
 */
export const makeDraftClaimLineItemAction = <Result extends ActionResult>(
  fn: ActionFn<DraftClaimLineItemContext, Result>,
) =>
  makeCrewClaimAction((args, appStore) => {
    const { draftId } = draftClaimLineItemsRouteParams.parse(args.params);
    const { draftClaimLineItem } = appStore.getState();
    const { crewClaim } = args.context;

    const draft =
      draftClaimLineItem && draftClaimLineItem.id === draftId ?
        draftClaimLineItem
      : crewClaim.claimLineItems.find((c) => c.id === draftId);

    if (!draft) {
      return new Error('Claim not found');
    }

    const context = {
      ...args.context,
      draftClaimLineItem: draft,
    };

    return fn({ ...args, context }, appStore);
  });

const draftClaimLineItemWithReasonSchema = draftClaimLineItemSchema.required({
  reasonId: true,
  reasonCategoryCode: true,
});

const draftClaimLineItemWithReasonContextSchema =
  draftClaimLineItemContextSchema.extend({
    draftClaimLineItem: draftClaimLineItemWithReasonSchema,
  });

type DraftClaimLineItemWithReasonContext = z.infer<
  typeof draftClaimLineItemWithReasonContextSchema
>;

/**
 * Creates a loader function that can be used with React Router for draft claim line item workflow with reason.
 * This loader function is designed to work with the crew claim context from the app store state that includes
 * `settings`, `order` as a crew order, `crewClaim`, either a draft claim line item or a claim line item from
 * the `crewClaim`, and a reason for the claim.
 * It provides a `useLoaderData` hook to access the data returned by the loader.
 * If the loader function returns a `Response`, it will be returned as is.
 * If the loader function returns an `Error`, it will be thrown and handled by React Router.
 * This loader function also validates the draft claim line item with reason context from the app store state or from the `crewClaim`.
 * @param fn - The loader function to be used with React Router.
 * The function should accept an object with `request`, `params`, and `context` properties, and the app store as the second argument.
 * The context will assert the draft claim line item with reason context schema, ensuring that the `settings`, `order` is a crew order,
 * `crewClaim` is present, and either a draft claim line item or a claim line item from the `crewClaim` is found,
 * and a reason for the claim is present.
 * The function should return a value that is compatible with `DataFunctionValue` from `react-router-dom`.
 * The function can also return an `Error` or a `Response`.
 * @returns An object with a `useLoaderData` hook to access the data returned by the loader,
 * and a `loader` function to be used with React Router.
 */
export const makeDraftClaimLineItemWithReasonLoader = <T extends Data>(
  fn: LoaderFn<DraftClaimLineItemWithReasonContext, T>,
) =>
  makeDraftClaimLineItemLoader((args, appStore) => {
    const { store, claimType, idFromPlatform } =
      draftClaimLineItemsRouteParams.parse(args.params);
    const parsedContext = draftClaimLineItemWithReasonContextSchema.safeParse(
      args.context,
    );

    if (!parsedContext.success) {
      logError('Draft claim line item with reason context schema failed', {
        extra: { error: parsedContext.error },
      });
      return redirect(`/${store}/order/${idFromPlatform}/${claimType}`);
    }

    return fn(
      {
        ...args,
        context: parsedContext.data,
      },
      appStore,
    );
  });

/**
 * Creates an action function that can be used with React Router for draft claim line item actions with reason.
 * This action function is designed to work with the crew claim context from the app store state that includes
 * `settings`, `order` as a crew order, `crewClaim`, either a draft claim line item or a claim line item from
 * the `crewClaim`, and a reason for the claim.
 * It provides a `useActionResult` hook to access the action result.
 * If the action function returns an `Error`, it will be thrown and handled by React Router.
 * This action function also validates the draft claim line item with reason context from the app store state or from the `crewClaim`.
 * @param fn - The action function to be used with React Router.
 * The function should accept an object with `request`, `params`, `formData`, and `context` properties, and the app store as the second argument.
 * The context will assert the draft claim line item with reason context schema, ensuring that the `settings`, `order` is a crew order,
 * `crewClaim` is present, and either a draft claim line item or a claim line item from the `crewClaim` is found,
 * and a reason for the claim is present.
 * The function should return a value that is compatible with `ActionResult`.
 * The function can also return an `Error`.
 * @returns An object with a `useActionResult` hook to access the action result,
 * and an `action` function to be used with React Router.
 */
export const makeDraftClaimLineItemWithReasonAction = <
  Result extends ActionResult,
>(
  fn: ActionFn<DraftClaimLineItemWithReasonContext, Result>,
) =>
  makeDraftClaimLineItemAction((args, appStore) => {
    const parsedContext = draftClaimLineItemWithReasonContextSchema.safeParse(
      args.context,
    );

    if (!parsedContext.success) {
      logError('Draft claim line item with reason context schema failed', {
        extra: { error: parsedContext.error },
      });
      return new Error(
        'Draft claim line item with reason context schema failed',
      );
    }

    return fn(
      {
        ...args,
        context: parsedContext.data,
      },
      appStore,
    );
  });
